import type { StreamResource } from '@/types/events'
import type { StreamTelemetryPoint } from '@/hooks/useStreamTelemetry'

export type CameraState =
  | 'invited'
  | 'connecting'
  | 'live'
  | 'reconnecting'
  | 'offline'
  | 'ended'

export function cameraState(
  stream: StreamResource,
  hasVideo: boolean,
  previouslyLive = false,
): CameraState {
  const { status, route_status: route } = stream.attributes
  if (hasVideo) return 'live'
  if (status === 'stopped' || status === 'stopping') return 'ended'
  if (status === 'error') return 'offline'
  if (route === 'grace' || status === 'recovery_pending') return 'reconnecting'
  if (route === 'failed' || route === 'terminated' || route === 'unhealthy')
    return 'offline'
  if (previouslyLive) return 'reconnecting'
  if (status === 'active' || status === 'provisioning' || route === 'active')
    return 'connecting'
  return 'invited'
}

export const CAMERA_STATE_LABELS: Record<CameraState, string> = {
  invited: 'Invited',
  connecting: 'Connecting',
  live: 'Live',
  reconnecting: 'Reconnecting',
  offline: 'Offline',
  ended: 'Ended',
}

export function isCamera(stream: StreamResource) {
  return stream.attributes.source_role !== 'programme'
}

export interface CameraLocation {
  stream: StreamResource
  latitude: number
  longitude: number
  fixed: boolean
  timestamp: string | null
}

function coordinates(
  latitude: unknown,
  longitude: unknown,
): { latitude: number; longitude: number } | null {
  if (
    latitude == null ||
    longitude == null ||
    latitude === '' ||
    longitude === ''
  )
    return null
  const lat = Number(latitude),
    lng = Number(longitude)
  return Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
    ? { latitude: lat, longitude: lng }
    : null
}

/** Location and video availability are independent. Fixed pins take precedence over telemetry. */
export function cameraLocations(
  streams: StreamResource[],
  telemetry: StreamTelemetryPoint[],
): CameraLocation[] {
  const points = new Map(telemetry.map((point) => [point.streamId, point]))
  return streams.filter(isCamera).flatMap<CameraLocation>((stream) => {
    const attrs = stream.attributes
    const pinned = coordinates(attrs.pinned_latitude, attrs.pinned_longitude)
    if (pinned) return [{ stream, ...pinned, fixed: true, timestamp: null }]
    const point = points.get(stream.id)
    const live = point && coordinates(point.latitude, point.longitude)
    const savedTime = Date.parse(attrs.last_location_at || '')
    const pointTime = Date.parse(point?.timestamp || '')
    if (
      live &&
      point &&
      (!Number.isFinite(savedTime) || pointTime >= savedTime)
    ) {
      return [{ stream, ...live, fixed: false, timestamp: point.timestamp }]
    }
    const persisted = coordinates(attrs.last_latitude, attrs.last_longitude)
    return persisted
      ? [
          {
            stream,
            ...persisted,
            fixed: false,
            timestamp: attrs.last_location_at ?? null,
          },
        ]
      : []
  })
}

export function locationLabel(
  location: CameraLocation | undefined,
  now = Date.now(),
): string {
  if (!location) return 'Location unavailable'
  if (location.fixed) return 'Fixed camera'
  const time = Date.parse(location.timestamp || '')
  if (!Number.isFinite(time)) return 'Last known location'
  const seconds = Math.max(0, Math.floor((now - time) / 1000))
  if (seconds < 15) return 'Location live'
  if (seconds < 60) return `Location ${seconds}s ago`
  if (seconds < 3600) return `Location ${Math.floor(seconds / 60)}m ago`
  return `Location ${Math.floor(seconds / 3600)}h ago`
}

export function workspaceError(error: unknown, fallback: string): string {
  const response = (
    error as {
      response?: { data?: { error?: { message?: string; errors?: string[] } } }
    }
  )?.response?.data?.error
  return (
    (Array.isArray(response?.errors) && response.errors.join('. ')) ||
    response?.message ||
    fallback
  )
}
